import axios from 'axios'
import { useAuthStore } from '@/stores/useAuthStore'
import { logError } from '@/utils/logger'

const isDevelopment = process.env.NODE_ENV !== "production"
const configuredApiUrl = process.env.VITE_API_URL?.trim()

if (!isDevelopment && !configuredApiUrl) {
  throw new Error('VITE_API_URL is required in production')
}

const baseURL = isDevelopment ? '/api' : configuredApiUrl?.replace(/\/+$/, '')

export const apiClient = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
})

let isRefreshing = false
let refreshSubscribers: Array<(token: string | null) => void> = []

const isAuthRoute = () => {
  if (typeof window === 'undefined') return false
  return window.location.pathname.startsWith('/auth')
}

const shouldSkipGlobalAuthRedirect = (requestUrl?: string) => {
  if (!requestUrl) return false
  return (
    requestUrl.includes('/auth/login') ||
    requestUrl.includes('/auth/refresh') ||
    isAuthRoute()
  )
}

function subscribeTokenRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb)
}

function onRefreshed(token: string | null) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}


apiClient.interceptors.request.use((config) => {
  try {
    // Strip request body for GET/HEAD to avoid sending data in GET requests
    const method = (config.method || 'get').toLowerCase()
    if ((method === 'get' || method === 'head') && 'data' in config) {
      const configWithData = config as { data?: unknown }
      delete configWithData.data
    }

    const token = useAuthStore.getState().token

    if (token && config && config.headers) {
      (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
    }
  } catch {
    // Silent error handling
  }
  return config
}, (error) => Promise.reject(error))


apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {

    const originalRequest = (error && error.config) ? error.config : null

    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const requestUrl = String(originalRequest.url ?? '')
      const skipGlobalRedirect = shouldSkipGlobalAuthRedirect(requestUrl)
      if (skipGlobalRedirect) {
        return Promise.reject(error)
      }
      originalRequest._retry = true

      if (isRefreshing) {
        // queue the request until refresh completes
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            if (token && originalRequest.headers) originalRequest.headers['Authorization'] = `Bearer ${token}`
            apiClient(originalRequest).then(resolve).catch(reject)
          })
        })
      }

      isRefreshing = true

      try {
        const response = await apiClient.post('/auth/refresh')
        const { access_token } = response.data || {}
        if (access_token) {
          try { useAuthStore.getState().setToken(access_token) } catch { /* ignore */ }
        }
        if (!access_token) {
          throw new Error('Refresh response did not include an access token')
        }

        onRefreshed(access_token ?? null)
        isRefreshing = false

        if (originalRequest.headers) originalRequest.headers['Authorization'] = `Bearer ${access_token}`
        return apiClient(originalRequest)

      } catch (refreshError) {
        logError('apiClient.refreshToken', refreshError)

        try {
          useAuthStore.getState().setToken(null)
        } catch { /* ignore */ }
        isRefreshing = false
        onRefreshed(null)

        // Redirect to login page
        if (typeof window !== 'undefined' && !skipGlobalRedirect) {
          window.location.href = '/auth/signin';
        }

        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)
